import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  Renderer2,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ADD_STEP_PREFIX,
  END_STEP_ID,
  EXPANDED_GROUP_END_PREFIX,
  EXPANDED_GROUP_START_PREFIX,
  GraphNode,
  GROUP_STEP_PREFIX,
  HIDDEN_STEP_PREFIX,
  PASSTHROUGH_STEP_PREFIX,
  SNIPPET_PREFIX,
  SNIPPET_STARTING_ID,
} from '../automation-graph';
import { AutomationEditorService } from '../automation-editor.service';
import { Automation, AutomationInterface, WorkflowStepInterface } from '@vendasta/automata';
import { AutomationViewService } from '../../automation/automation-view.service';
import { CommonNodeComponent, NodeType } from '../components/common/common-node.component';
import { AddStepComponent, PasteEvent } from '../components/add-step/add-step.component';
import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';
import { EndStepComponent } from '../components/end-step/end-step.component';
import { TRIGGER_STEP_ID } from '../../common/constants';
import { ZoomControlComponent } from '../zoom-control/zoom-control.component';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { BranchNameBadgeComponent } from '../components/branch-name-badge/branch-name-badge.component';
import { BranchUtilities } from '../branch-utilities';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Branch, NONE, RIGHT } from '../component-loader/branch-options';
import { NodeActionsService } from '../services/node-actions.service';
import { ChangeSideMenuContent, WorkflowViewerV2Service } from './workflow-viewer-v2.service';
import { GroupNodeComponent } from '../components/group-node/group-node.component';
import { combineLatest, from, Observable } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { EmptyNodeComponent } from '../components/empty-node/empty-node.component';
import { AutomationGraphUtilitiesService } from '../automation-graph-utilities.service';
import { CurrentAutomationService } from '@galaxy/automata/shared';

const VergeAndConvergePaddingPx = 20;

const widthOfActionPx = 380;
const widthOfGroupPx = 380;
const widthOfAddStepPx = 104;

const defaultVerticalDistanceBetweenNodesPx = 50;
const halfMinHorizontalSpacingBetweenNodesPx = 20;

const actionWidth = widthOfActionPx + halfMinHorizontalSpacingBetweenNodesPx * 2;

enum LineStyle {
  solid = 0,
  hidden = 1,
}

interface BranchNameConfig {
  branch: Branch;
  text: string;
}

@Component({
  selector: 'automata-workflow-viewer-v2',
  templateUrl: './workflow-viewer-v2.component.html',
  styleUrls: ['./workflow-viewer-v2.component.scss'],
  providers: [],
  imports: [CommonModule, ZoomControlComponent, GalaxyButtonGroupModule, GalaxyBadgeModule],
})
export class WorkflowViewerV2Component implements OnInit, AfterViewInit {
  @Input() automationId = '';
  @Input() readOnly = false;
  @Output() changeSideMenuContent: EventEmitter<ChangeSideMenuContent> = new EventEmitter<ChangeSideMenuContent>();

  automation$: Observable<Automation> = this.currentAutomationService.automation$;

  isStepAdditionAndEditingDisabled$: Observable<boolean> = combineLatest([
    this.automation$,
    this.automation$.pipe(switchMap((a) => this.automationViewService.isEditable$(a))),
    from(toObservable(this.nodeActionsService.grouping)),
  ]).pipe(
    map(([automation, isEditable, isGrouping]) => {
      return this.readOnly || !isEditable || !automation?.triggerStep || isGrouping;
    }),
  );
  invertedIsStepAdditionAndEditingDisabled$: Observable<boolean> = this.isStepAdditionAndEditingDisabled$.pipe(
    map((value) => !value),
  );
  isSnippet = false;

  protected editorViewerPortID: string;
  protected zoomWrapperID: string;
  protected graphViewerWrapperID: string;
  protected graSVGContainerID: string;
  protected graphNodeViewerID: string;

  constructor(
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly graphUtilities: AutomationGraphUtilitiesService,
    private readonly automationEditorService: AutomationEditorService,
    private readonly automationViewService: AutomationViewService,
    private readonly nodeActionsService: NodeActionsService,
    private readonly translateService: TranslateService,
    private readonly destroyRef: DestroyRef,
    protected readonly workflowViewerService: WorkflowViewerV2Service,
    private readonly cdr: ChangeDetectorRef,
    private readonly currentAutomationService: CurrentAutomationService,
  ) {
    effect(() => {
      // Rendering our graph
      const graphMap = this.automationEditorService.graphMap();
      const stepMap = this.automationEditorService.stepMap();
      const automation = this.currentAutomationService.automation();

      // nodeViewer.clear() is async, so we need to wait for it to finish before we can start placing nodes
      this.nodeViewer?.clear();
      if (this.isSnippet) {
        this.placeNodes(graphMap, stepMap, automation);
      } else {
        waitForElementToNotExist(TRIGGER_STEP_ID).then(() => {
          this.placeNodes(graphMap, stepMap, automation);
        });
      }
    });
  }

  ngOnInit(): void {
    if (this.automationId) {
      this.currentAutomationService.init(this.automationId);
      this.isSnippet = this.automationId.startsWith(SNIPPET_STARTING_ID);

      this.editorViewerPortID = this.workflowViewerService.getEditorViewportId(this.isSnippet);
      this.zoomWrapperID = this.workflowViewerService.getZoomWrapperId(this.isSnippet);
      this.graphViewerWrapperID = this.workflowViewerService.getGraphViewerWrapperId(this.isSnippet);
      this.graSVGContainerID = this.workflowViewerService.getGraphSvgContainerId(this.isSnippet);
      this.graphNodeViewerID = this.workflowViewerService.getGraphNodeViewerId(this.isSnippet);
    }
  }

  zoomLevel = 10;
  allNodes: ElementRef<HTMLElement>[] = [];

  isFirstDraw = true;

  @ViewChild('svgContainer') svgContainer: ElementRef<SVGElement>;
  @ViewChild('nodeViewer', { read: ViewContainerRef }) nodeViewer: ViewContainerRef;

  ngAfterViewInit() {
    waitForElementToExist(this.editorViewerPortID).then(() => {
      this.setupScrolling();
    });
  }

  centerMySelfToTrigger(): void {
    const centerStepID = this.isSnippet
      ? SNIPPET_PREFIX + this.findStartingNode(this.currentAutomationService.automation()?.workflow)?.id
      : TRIGGER_STEP_ID;
    this.workflowViewerService.centerMyselfToStep(centerStepID, this.isSnippet);
  }

  setupScrolling(): void {
    const editorViewPort = this.document.getElementById(this.editorViewerPortID);
    const backgroundPatternCircles = this.document.getElementById('pattern-circles');
    const graphWrapper = this.document.getElementById(this.graphViewerWrapperID);
    const zoomWrapper = this.document.getElementById(this.zoomWrapperID);
    if (!editorViewPort || !backgroundPatternCircles || !graphWrapper || !zoomWrapper) {
      console.error('Could not find the editor view port, background, graph viewer wrapper, or zoom wrapper');
      return;
    }

    const workflowViewerService = this.workflowViewerService;
    // setup mouse wheel scrolling
    const wheelHandler = function (e: WheelEvent): void {
      if (workflowViewerService.isMouseInsideScrollingActionContent(e.target as HTMLElement)) {
        // Don't scroll the graph if they are trying to scroll the overflow text of an action
        return;
      }

      let diffX = -e.deltaX;
      let diffY = -e.deltaY;
      if (pos.curScale !== 1) {
        // When zoomed the scrolling needs to be scaled up
        diffX = -e.deltaX / pos.curScale;
        diffY = -e.deltaY / pos.curScale;
      }

      const graphStyle = window.getComputedStyle(graphWrapper);
      const graphMatrix = new WebKitCSSMatrix(graphStyle.transform);

      // Move the graph and move the circle background
      graphWrapper.style.transform = `translate(${graphMatrix.m41 + diffX}px, ${graphMatrix.m42 + diffY}px)`;
      backgroundPatternCircles?.setAttribute('x', `${graphMatrix.m41 + diffX}`);
      backgroundPatternCircles?.setAttribute('y', `${graphMatrix.m42 + diffY}`);
    };

    // Set up the drag scrolling
    let pos = { mouseX: 0, mouseY: 0, xMatrix: 0, yMatrix: 0, curScale: 1 };
    const mouseOrTouchDownHandler = function (e: MouseEvent | TouchEvent): void {
      // Acquire the "click" position for either mouse or touch
      let clientX = 0;
      let clientY = 0;
      if (e instanceof MouseEvent) {
        // 1 is a left click
        if (e.buttons !== 1) {
          return;
        }
        clientX = e.clientX;
        clientY = e.clientY;
      } else {
        const touch = e.touches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
      }

      editorViewPort.style.cursor = 'grabbing';
      editorViewPort.style.userSelect = 'none';

      const graphStyle = window.getComputedStyle(graphWrapper);
      const graphMatrix = new WebKitCSSMatrix(graphStyle.transform);

      const zoomStyle = window.getComputedStyle(zoomWrapper);
      const zoomMatrix = new WebKitCSSMatrix(zoomStyle.transform);
      pos = {
        // Get the current mouse position
        mouseX: clientX,
        mouseY: clientY,
        // get the current translation value
        xMatrix: graphMatrix.m41,
        yMatrix: graphMatrix.m42,
        curScale: zoomMatrix.m22,
      };

      document.addEventListener('mousemove', mouseOrTouchMoveHandler);
      document.addEventListener('touchmove', mouseOrTouchMoveHandler);
      document.addEventListener('mouseup', mouseOrTouchUpHandler);
      document.addEventListener('touchend', mouseOrTouchUpHandler);
    };

    const mouseOrTouchMoveHandler = function (e: MouseEvent | TouchEvent): void {
      let clientX = 0;
      let clientY = 0;
      if (e instanceof MouseEvent) {
        clientX = e.clientX;
        clientY = e.clientY;
      } else {
        const touch = e.touches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
      }
      // How far the mouse has been moved
      const dx = clientX - pos.mouseX;
      const dy = clientY - pos.mouseY;

      let diffX = dx;
      let diffY = dy;
      if (pos.curScale !== 1) {
        // When zoomed the scrolling needs to be scaled up
        diffX = dx / pos.curScale;
        diffY = dy / pos.curScale;
      }

      // Move the graph and move the circle background
      graphWrapper.style.transform = `translate(${pos.xMatrix + diffX}px, ${pos.yMatrix + diffY}px)`;
      backgroundPatternCircles?.setAttribute('x', `${pos.xMatrix + diffX}`);
      backgroundPatternCircles?.setAttribute('y', `${pos.yMatrix + diffY}`);
    };

    const mouseOrTouchUpHandler = function (): void {
      editorViewPort.style.cursor = 'grab';
      document.removeEventListener('mousemove', mouseOrTouchMoveHandler);
      document.removeEventListener('touchmove', mouseOrTouchMoveHandler);
      document.removeEventListener('mouseup', mouseOrTouchUpHandler);
      document.removeEventListener('touchend', mouseOrTouchUpHandler);
    };

    // Attach the handlers
    editorViewPort.addEventListener('mousedown', mouseOrTouchDownHandler);
    editorViewPort.addEventListener('touchstart', mouseOrTouchDownHandler);
    editorViewPort.addEventListener('wheel', wheelHandler);
  }

  private placeNodes(
    graphMap: Map<string, GraphNode>,
    stepMap: Map<string, WorkflowStepInterface>,
    automation: any,
  ): void {
    if (!automation) {
      return;
    }

    this.allNodes = [];
    let step;
    // a common function id, type, automation, step,
    if (this.isSnippet) {
      step = this.findStartingNode(automation?.workflow);
      // create first action here instead of trigger
      this.createFirstComponent(step, step?.id, 'task', automation, graphMap, stepMap);
    } else {
      // Start with creating and placing the trigger node
      step = stepMap?.get(TRIGGER_STEP_ID);
      this.createFirstComponent(step, TRIGGER_STEP_ID, 'trigger', automation, graphMap, stepMap);
    }
  }

  private placeChildren(
    parentId: string,
    stepMap: Map<string, WorkflowStepInterface>,
    graphMap: Map<string, GraphNode>,
  ): void {
    const children = this.graphUtilities.getChildrenNodes(parentId);
    // If there are no children, check if we're done placing everything
    if (!children?.length) {
      if (this.allNodes.length === this.graphUtilities.getAllNodes()?.length) {
        this.allNodesPlaced();
      }
      return;
    }
    if (children.length === 1) {
      this.placeSingleChild(children[0], stepMap, graphMap, 0, 0);
      return;
    }

    // Otherwise we have multiple children to place

    const middleIndex = (children.length - 1) / 2;
    const middleChild = Math.ceil(middleIndex);
    const isEvenNumberOfChildren = children.length % 2 === 0;

    // Placing of children needs to happen from middle outwards
    for (let i = middleChild; i < children.length; i++) {
      const horizontalOffset = this.calculateHorizontalOffset(i, middleIndex, isEvenNumberOfChildren);
      // For multi branching nodes, we only want to push further to the right the difference between us and the child beside us
      // If we keep pushing from the center we push too far
      let pushOffset = horizontalOffset;
      if (i - 1 >= middleChild) {
        pushOffset = horizontalOffset - this.calculateHorizontalOffset(i - 1, middleIndex, isEvenNumberOfChildren);
      }

      this.placeSingleChild(
        children[i],
        stepMap,
        graphMap,
        horizontalOffset,
        pushOffset,
        this.buildBranchNameConfig(stepMap.get(parentId), i, children.length),
      );
    }

    // Placing of children needs to happen from middle outwards
    for (let i = middleChild - 1; i >= 0; i--) {
      const horizontalOffset = -this.calculateHorizontalOffset(i, middleIndex, isEvenNumberOfChildren);
      let pushOffset = horizontalOffset;
      if (i + 1 < middleChild) {
        pushOffset = horizontalOffset + this.calculateHorizontalOffset(i + 1, middleIndex, isEvenNumberOfChildren);
      }

      this.placeSingleChild(
        children[i],
        stepMap,
        graphMap,
        horizontalOffset,
        pushOffset,
        this.buildBranchNameConfig(stepMap.get(parentId), i, children.length),
      );
    }
  }

  private buildBranchNameConfig(
    step: WorkflowStepInterface | undefined,
    childIndex: number,
    numberOfChildren: number,
  ): BranchNameConfig | undefined {
    if (!step || numberOfChildren <= 1) {
      return undefined;
    }
    let branch: Branch = NONE;
    if (numberOfChildren > 1) {
      branch = childIndex === numberOfChildren - 1 ? RIGHT : childIndex;
    }
    const resp = BranchUtilities.getBranchTextKey(step, branch);
    const text = this.translateService.instant(resp[0], { number: resp[1]?.number });
    return { text: text, branch: branch };
  }

  // calculateHorizontalOffset always returns a positive number
  private calculateHorizontalOffset(childIndex: number, middleIndex: number, isEvenNumberOfChildren: boolean): number {
    let horizontalOffset = 0;
    if (childIndex !== middleIndex) {
      const distanceFromCenter = Math.ceil(Math.abs(childIndex - middleIndex));
      horizontalOffset += actionWidth * distanceFromCenter;
    }
    if (isEvenNumberOfChildren) {
      horizontalOffset -= actionWidth / 2;
    }
    return horizontalOffset;
  }

  private placeSingleChild(
    childId: string,
    stepMap: Map<string, WorkflowStepInterface>,
    graphMap: Map<string, GraphNode>,
    horizontalOffset: number,
    pushOffset: number,
    branchNameConfig?: BranchNameConfig,
  ): void {
    if (!this.isSnippet && document.getElementById(childId)) {
      return;
    } else if (this.isSnippet && document.getElementById(SNIPPET_PREFIX + childId)) {
      // snippet elements are placed with snippet- prefix
      return;
    }

    const parentIds = this.graphUtilities.getParentNodes(childId);
    const parents: HTMLElement[] = [];
    for (let i = 0; i < parentIds.length; i++) {
      const parent = this.isSnippet
        ? this.document.getElementById(SNIPPET_PREFIX + parentIds[i])
        : this.document.getElementById(parentIds[i]);
      if (!parent) {
        // Wait for all the parents to exist before drawing the child.
        return;
      }
      parents.push(parent);
    }

    const step = stepMap.get(childId);
    let componentToCreate: Type<any> = CommonNodeComponent;
    if (
      childId.startsWith(ADD_STEP_PREFIX) ||
      childId.startsWith(HIDDEN_STEP_PREFIX) ||
      childId.startsWith(PASSTHROUGH_STEP_PREFIX)
    ) {
      componentToCreate = AddStepComponent;
    } else if (
      this.showGroup(step?.groupId) ||
      childId.startsWith(GROUP_STEP_PREFIX) ||
      childId.startsWith(EXPANDED_GROUP_START_PREFIX)
    ) {
      componentToCreate = GroupNodeComponent;
    } else if (childId === END_STEP_ID) {
      componentToCreate = EndStepComponent;
    } else if (childId.startsWith(EXPANDED_GROUP_END_PREFIX)) {
      componentToCreate = EmptyNodeComponent;
    }

    const newNodeComponent = this.nodeViewer.createComponent(componentToCreate);
    newNodeComponent.location.nativeElement.id = this.isSnippet ? SNIPPET_PREFIX + childId : childId;
    newNodeComponent.instance.type = 'task';
    newNodeComponent.instance.step = step;
    newNodeComponent.instance.groupId = this.getGroupId(stepMap, parentIds, step, childId);
    newNodeComponent.instance.precedingSteps = this.graphUtilities
      .getImmediateParents(childId)
      .filter((id) => id !== TRIGGER_STEP_ID);
    newNodeComponent.instance.branch = branchNameConfig ? branchNameConfig.branch : NONE;
    newNodeComponent.instance.automation = this.currentAutomationService.getCurrentAutomation();
    newNodeComponent.instance.enabled = !this.readOnly;
    newNodeComponent.instance.disabled = this.isStepAdditionAndEditingDisabled$;
    this.invertedIsStepAdditionAndEditingDisabled$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (value) => {
        newNodeComponent.instance.enabled = value;
      },
    });
    if (childId.startsWith(HIDDEN_STEP_PREFIX) || childId.startsWith(PASSTHROUGH_STEP_PREFIX)) {
      newNodeComponent.location.nativeElement.style.visibility = 'hidden';
    }
    newNodeComponent.location.nativeElement.style.position = `absolute`; // Can remove when we get rid of original editor
    newNodeComponent.instance.changeSideMenuContent
      ?.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe((event: ChangeSideMenuContent) => {
        this.changeSideMenuContent.emit(event);
      });
    newNodeComponent.instance.pasteStepClick
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: PasteEvent) => {
        this.nodeActionsService.paste(event.branch, event.precedingSteps, newNodeComponent.instance.groupId);
      });
    newNodeComponent.instance.goToStepClicked?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((stepId: string) => {
      this.workflowViewerService.centerMyselfToStep(stepId, this.isSnippet);
    });

    // Calculate the top position of the new node
    let lowestParentBottomPx = 0;
    for (let i = 0; i < parents.length; i++) {
      const parentId = parents[i].id;
      const parentBottomPx = this.getBottomPositionGivenTopY(
        parents[i].offsetTop,
        this.workflowViewerService.heightOfNode(parentId, stepMap),
      );
      if (parentBottomPx > lowestParentBottomPx) {
        lowestParentBottomPx = parentBottomPx;
      }
    }
    newNodeComponent.location.nativeElement.style.top = `${lowestParentBottomPx + defaultVerticalDistanceBetweenNodesPx}px`;

    // Calculate the left position of the new node
    const allCenterPositions = parents.reduce((acc, parent) => {
      return acc + this.getCenterPositionGivenLeftX(parent.offsetLeft, this.widthOfNode(parent.id, stepMap));
    }, 0);
    const parentCenter = allCenterPositions / parents.length;
    const leftPosition = this.getLeftPositionGivenCenterX(parentCenter, this.widthOfNode(childId, stepMap));
    newNodeComponent.location.nativeElement.style.left = `${leftPosition + horizontalOffset}px`;

    const myCenter = parentCenter + horizontalOffset;
    this.pushNodesToTheSide(childId, myCenter, pushOffset);

    this.allNodes.push(newNodeComponent.location);

    // Add a badge for the name of the branch
    if (branchNameConfig) {
      const branchNameComponent = this.nodeViewer.createComponent(BranchNameBadgeComponent);
      branchNameComponent.instance.text = branchNameConfig.text;
      branchNameComponent.instance.color = 'grey';
      newNodeComponent.location.nativeElement.appendChild(branchNameComponent.location.nativeElement);
    }
    const idToWaitFor = this.isSnippet ? SNIPPET_PREFIX + childId : childId;
    waitForElementToExist(idToWaitFor).then(() => {
      this.placeChildren(childId, stepMap, graphMap);
    });
  }

  private createFirstComponent(
    step: WorkflowStepInterface,
    id: string,
    type: NodeType,
    automation: AutomationInterface,
    graphMap: Map<string, GraphNode>,
    stepMap: Map<string, WorkflowStepInterface>,
  ) {
    const firstComponent = this.nodeViewer.createComponent(CommonNodeComponent);
    const componentID = this.isSnippet ? SNIPPET_PREFIX + id : id;
    firstComponent.instance.type = type;
    firstComponent.instance.step = step;
    firstComponent.instance.precedingSteps = [];
    firstComponent.instance.branch = 'NONE';
    firstComponent.instance.automation = automation;
    firstComponent.instance.enabled = !this.readOnly;
    firstComponent.location.nativeElement.id = componentID;
    firstComponent.location.nativeElement.style.top = `50px`;
    firstComponent.location.nativeElement.style.left = `0px`;
    firstComponent.location.nativeElement.style.position = `absolute`; // Can remove when we get rid of original editor
    firstComponent.instance.changeSideMenuContent
      ?.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe((event: ChangeSideMenuContent) => {
        this.changeSideMenuContent.emit(event);
      });
    this.allNodes.push(firstComponent.location);

    waitForElementToExist(componentID).then(() => {
      this.placeChildren(id, stepMap, graphMap);
    });
  }

  private getGroupId(
    stepMap: Map<string, WorkflowStepInterface>,
    parentIds: string[],
    step: WorkflowStepInterface | undefined,
    childId: string,
  ) {
    switch (true) {
      case childId.startsWith(EXPANDED_GROUP_START_PREFIX):
        return childId.replace(EXPANDED_GROUP_START_PREFIX, '');
      case childId.startsWith(EXPANDED_GROUP_END_PREFIX):
        return childId.replace(EXPANDED_GROUP_END_PREFIX, '');
      case childId.startsWith(GROUP_STEP_PREFIX):
        return childId.replace(GROUP_STEP_PREFIX, '');
      case childId.startsWith(ADD_STEP_PREFIX):
        for (const parentId of parentIds) {
          if (parentId.startsWith(EXPANDED_GROUP_START_PREFIX)) {
            return parentId.replace(EXPANDED_GROUP_START_PREFIX, '');
          }
          if (stepMap.get(parentId)?.groupId) {
            return stepMap.get(parentId)?.groupId;
          }
        }
        return '';
      default:
        return step?.groupId;
    }
  }

  private showGroup(groupId = '') {
    return groupId && !this.automationViewService.expandedGroups().has(groupId);
  }

  // If a node is not being placed directly below its parent, it needs to move all the other nodes to give it space
  private pushNodesToTheSide(nodeId: string, myCenter: number, offsetToPush: number): void {
    if (!offsetToPush) {
      return;
    }

    // If a sibling node is already to the side this far we don't need to push again
    const nodesInSameBranchLane = this.graphUtilities.getAllNodesThatAreInTheSameBranchLaneAsMe(nodeId);
    for (const nodeId of nodesInSameBranchLane) {
      const node = this.getNodeInAllNodes(nodeId);
      const nodeCenter = (node?.nativeElement?.offsetLeft || 0) + (node?.nativeElement?.offsetWidth || 0) / 2;
      if (offsetToPush > 0 && nodeCenter >= myCenter) {
        return;
      } else if (offsetToPush < 0 && nodeCenter <= myCenter) {
        return;
      }
    }

    const myRootBranch = this.graphUtilities.getRootBranchForNode(nodeId);
    for (const node of this.allNodes) {
      if (nodesInSameBranchLane.has(node.nativeElement.id)) {
        // Disregard nodes in the same branch lane
        continue;
      }
      if (this.graphUtilities.getRootBranchForNode(node.nativeElement.id) !== myRootBranch) {
        // Disregard nodes in different root branches
        continue;
      }
      const nodeCenter = node.nativeElement.offsetLeft + node.nativeElement.offsetWidth / 2;
      if ((offsetToPush > 0 && nodeCenter >= myCenter) || (offsetToPush < 0 && nodeCenter <= myCenter)) {
        // Push node to the side
        node.nativeElement.style.left = `${node.nativeElement.offsetLeft + offsetToPush}px`;
        // If node is our root branching node, push all nodes above it to keep things in line
        if (this.graphUtilities.isRootBranchingNode(node.nativeElement.id)) {
          this.pushAllNodesAboveHeight(node.nativeElement.offsetTop, offsetToPush);
        }
      }
    }
  }

  // Help find the first node of the workflow. Useful when viewing snippet
  private findStartingNode(workflow: WorkflowStepInterface[]): WorkflowStepInterface {
    const defaultStepIds = new Set<string>();
    workflow.forEach((step) => {
      if (step.choices?.defaultNextStep) {
        defaultStepIds.add(step.choices.defaultNextStep);
      }
    });
    const startingNode = workflow.find((step) => step.id && !defaultStepIds.has(step.id));
    return startingNode;
  }

  private getNodeInAllNodes(nodeId: string): ElementRef<HTMLElement> | undefined {
    return this.allNodes.find((node) => node.nativeElement.id === nodeId);
  }

  private pushAllNodesAboveHeight(height: number, offsetToPush: number): void {
    this.allNodes.forEach((node) => {
      if (node.nativeElement.offsetTop < height) {
        node.nativeElement.style.left = `${node.nativeElement.offsetLeft + offsetToPush}px`;
      }
    });
  }

  private widthOfNode(nodeId: string, stepMap: Map<string, WorkflowStepInterface>): number {
    if (nodeId.startsWith(SNIPPET_PREFIX)) {
      // the snippet editor components are placed with prefix snippet- and removing it here ensures that
      // snippet prefix doesn't interfere with getting width for generic nodes
      nodeId = nodeId.replace(SNIPPET_PREFIX, '');
    }
    if (
      nodeId.startsWith(ADD_STEP_PREFIX) ||
      nodeId.startsWith(HIDDEN_STEP_PREFIX) ||
      nodeId.startsWith(PASSTHROUGH_STEP_PREFIX)
    ) {
      return widthOfAddStepPx;
    } else if (
      this.showGroup(stepMap.get(nodeId)?.groupId) ||
      nodeId.startsWith(GROUP_STEP_PREFIX) ||
      nodeId.startsWith(EXPANDED_GROUP_START_PREFIX)
    ) {
      return widthOfGroupPx;
    } else if (nodeId === END_STEP_ID) {
      return 80;
    }
    return widthOfActionPx;
  }

  private getBottomPositionGivenTopY(y: number, height: number): number {
    return y + height;
  }

  private getCenterPositionGivenLeftX(x: number, width: number): number {
    return x + width / 2;
  }

  private getLeftPositionGivenCenterX(x: number, width: number): number {
    return x - width / 2;
  }

  // Things to do after all the nodes have been created and placed
  private allNodesPlaced(): void {
    if (this.isFirstDraw) {
      this.isFirstDraw = false;
      this.centerMySelfToTrigger();
    } else if (this.isSnippet) {
      this.centerMySelfToTrigger();
    }
    this.drawLines();
    this.cdr.detectChanges();
  }

  private drawLines(): void {
    // clear existing lines to make way for new ones
    this.clearLines();

    const edges = this.graphUtilities.getAllEdges();

    for (const edge of edges) {
      let lineStyle = LineStyle.solid;
      if (this.graphUtilities.isTerminatingStep(edge.v) || edge.v.startsWith(HIDDEN_STEP_PREFIX)) {
        lineStyle = LineStyle.hidden;
      }
      this.drawArrowBetweenSteps(edge.v, edge.w, lineStyle);
    }
  }

  drawArrowBetweenSteps(parentId: string, childId: string, lineStyle: LineStyle): void {
    // Both nodes need to exist in the DOM to draw a line between them
    const parent = this.isSnippet
      ? this.document.getElementById(SNIPPET_PREFIX + parentId)
      : this.document.getElementById(parentId);
    const idToSearch = this.isSnippet ? SNIPPET_PREFIX + childId : childId;
    if (!parent) {
      waitForElementToExist(idToSearch).then(() => {
        this.drawArrowBetweenSteps(parentId, childId, lineStyle);
      });
      return;
    }
    const child = this.document.getElementById(idToSearch);

    if (!child) {
      waitForElementToExist(idToSearch).then(() => {
        this.drawArrowBetweenSteps(parentId, childId, lineStyle);
      });
      return;
    }

    if (lineStyle === LineStyle.hidden) {
      return;
    }

    const parentMiddleXPos = parent.offsetLeft + parent.offsetWidth / 2;
    const parentTopYPos = parent.offsetTop;

    const childMiddleXPos = child.offsetLeft + child.offsetWidth / 2;
    const childTopYPos = child.offsetTop;

    // Draw the line
    const path = this.renderer.createElement('path', 'svg');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', '#bdbdbd');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('z-index', '1');

    // Drawn line going down, then across, then down
    // For nodes right above each other, the 'across' will not draw anything
    path.setAttribute(
      'd',
      `M ${parentMiddleXPos} ${parentTopYPos} V ${childTopYPos - VergeAndConvergePaddingPx} H ${childMiddleXPos} V ${childTopYPos}`,
    );
    this.svgContainer.nativeElement.appendChild(path);

    if (
      childId.startsWith(ADD_STEP_PREFIX) ||
      childId.startsWith(PASSTHROUGH_STEP_PREFIX) ||
      childId.startsWith(EXPANDED_GROUP_END_PREFIX)
    ) {
      // Don't draw arrows pointing to these steps
      return;
    }
    // Draw the arrow
    const arrow = this.renderer.createElement('polygon', 'svg');
    arrow.setAttribute('stroke', '#bdbdbd');
    arrow.setAttribute('fill', '#bdbdbd');
    arrow.setAttribute(
      'points',
      `${childMiddleXPos},${childTopYPos} ${childMiddleXPos - 5},${childTopYPos - 5} ${childMiddleXPos + 5},${childTopYPos - 5}`,
    );
    this.svgContainer.nativeElement.appendChild(arrow);
  }

  clearLines(): void {
    const childElements = this.svgContainer.nativeElement.children;
    for (const child of childElements) {
      this.renderer.removeChild(this.svgContainer.nativeElement, child);
    }
  }
}

// Wait for the element to exist in the DOM https://stackoverflow.com/questions/5525071/how-to-wait-until-an-element-exists
// This lets us use OffSetLeft and OffSetTop to place the nodes. If this is too slow we can calculate the positions based on static values
function waitForElementToExist(selector: string): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    if (document.getElementById(selector)) {
      return resolve(document.getElementById(selector));
    }

    const observer = new MutationObserver(() => {
      if (document.getElementById(selector)) {
        observer.disconnect();
        resolve(document.getElementById(selector));
      }
    });

    observer.observe(document, {
      childList: true,
      subtree: true,
    });
  });
}

function waitForElementToNotExist(selector: string): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    if (!document.getElementById(selector)) {
      return resolve(null);
    }

    const observer = new MutationObserver(() => {
      if (!document.getElementById(selector)) {
        observer.disconnect();
        resolve(null);
      }
    });

    observer.observe(document, {
      childList: true,
      subtree: true,
    });
  });
}
